import React, { useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { InputText } from 'primereact/inputtext';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import { getById, uploadLogo } from 'app/api/insuranceCompanyApi';
import { FileUpload } from 'primereact/fileupload';
import { Checkbox } from 'primereact/checkbox';

const validationSchema = Yup.object({
  name: Yup.string().max(255, 'Max 255 znakov').required('Názov je povinný'),
});

const CreateInsuranceCompany: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL
  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      getById(id)
        .then(response => {
          if (response.data) {
            setCompany(response.data);
          }
          setLoading(false);
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
          setLoading(false);
        });
    }
  }, []);

  // Handle form submission
  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;
    if (id) {
      call = axios.put(`/api/insurance-companies/${id}`, values);
    } else {
      call = axios.post('/api/insurance-companies', values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Spoločnosť úspešne uložená.');
        setCompany(response.data);
        setIsSaving(false);
        navigate('/settings/insurance-company');
      })
      .catch(error => {
        setIsSaving(false);
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      });
  };

  const handleFileUpload = event => {
    const files = event.files;

    setLoading(true);
    uploadLogo(company?.id, files[0])
      .then(response => {
        setCompany(response.data);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta spoločnosti' : 'Vytvoriť novú spoločnosť'}</h2>
          <Formik
            initialValues={
              company
                ? company
                : {
                    number: '',
                    iban: '',
                  }
            }
            validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="name">Názov spoločnosti</label>
                        <Field name="name" as={InputText} />
                        <ErrorMessage name="name" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="iban">IBANy alebo čísla účtov rozdelené čiarkou</label>
                        <Field name="iban" as={InputText} />
                        <ErrorMessage name="iban" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="iban">Generovať provízie po importe platby</label>
                        <Checkbox
                          onChange={event => void setFieldValue('generovatProviziePoImportePlatieb', event.checked)}
                          checked={values.generovatProviziePoImportePlatieb as boolean}
                        ></Checkbox>
                        <ErrorMessage name="generovatProviziePoImportePlatieb" component="div" className="p-error" />
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="p-field">
                        <label htmlFor="logo">Logo</label>
                        <FileUpload
                          mode="basic"
                          multiple={false}
                          auto={true}
                          accept=".png,.jpg,.jpeg"
                          name="files"
                          customUpload
                          uploadHandler={handleFileUpload}
                          chooseLabel="Nahrať logo"
                          uploadLabel="Nahrať"
                          cancelLabel="Zrušiť"
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-field">
                        {company.logo && company.id && (
                          <img
                            src={`https://system.nfagroup.eu/api/insurance-companies/logo/${company.id}`}
                            style={{ maxWidth: '300px', height: 'auto' }}
                            alt={company.name}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    style={{ backgroundColor: '#e31818' }}
                    label="Uložiť"
                    icon="pi pi-check"
                    className="p-mt-3"
                    disabled={isSaving}
                  />
                  <Button
                    type="button"
                    style={{ marginTop: '10px' }}
                    label="Naspäť"
                    icon="pi pi-arrow-left"
                    className="p-button-secondary"
                    onClick={() => navigate('/settings/insurance-company')}
                  />
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </div>
  );
};

export default CreateInsuranceCompany;
